import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import moment from "moment";
import { formatPhoneNumber, getCustomerName, getFormattedAddress } from "../../common/util";
import OrderReceiptPrintLineItems from "../../order/component/print/OrderReceiptPrintLineItems";
import { BAKER_PIC_FOLDER } from "../../common/constants";
import currency from "currency.js";
import CloseIcon from "@mui/icons-material/Close";
import StorePolicy from "../../common/component/StorePolicy";

export default function OrderReceiptPrint(props) {
  const printRef = useRef();
  const { order } = props;

  const [open, setOpen] = useState(false);

  const print = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${getCustomerName(order.customer)} ${moment(order.fulfillment.date).format("MMM DD, YYYY")}`,
  });

  const handlePrint = () => {
    print();
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} fullWidth color="secondary" variant="outlined">
        Print Detailed Receipt
      </Button>
      {open && (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth={true} maxWidth="sm">
          <DialogTitle>
            Receipt • {getCustomerName(order.customer)} •{" "}
            {moment(order.fulfillment.date).format("MMM DD, YYYY")}
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box mt={2} ref={printRef}>
              <Box mb={4} textAlign={"center"}>
                <img
                  style={{ width: "50px", height: "50px", borderRadius: "5px" }}
                  src={`${BAKER_PIC_FOLDER}/${order.baker.id}/logo400.webp`}
                />
                <Typography>{order.baker.name}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "15px", justifyContent: "space-between" }}>
                <Typography variant="overline">
                  {getCustomerName(order.customer)} • {formatPhoneNumber(order.customer.phone)}
                </Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  {moment(order.ts._seconds * 1000).format("M/D/YY h:mm a")}
                </Typography>
              </Box>
              <Box>
                <OrderReceiptPrintLineItems items={order.items} />
                <Box py={2} sx={{ borderBottom: "1px dashed #DEDEDE" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={10}>
                      <Box mb={1}>
                        <Typography variant="body2" gutterBottom>
                          {order.fulfillment.type === "delivery" ? "Delivery" : "Store Pickup"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption">
                          {moment(order.fulfillment.date).format("ddd, MMM D, YYYY")} •{" "}
                          {order.fulfillment.time}
                        </Typography>
                        <Box>
                          <Typography variant="caption">
                            {getFormattedAddress(order.fulfillment.location)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={2} textAlign={"right"}>
                      <Typography variant="overline">
                        {currency(order.payment.delivery || "0").format()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box py={2}>
                  <Grid container spacing={2}>
                    {+order.payment.discount > 0 && (
                      <>
                        <Grid item xs={10}>
                          <Typography variant="body2">Discount</Typography>
                        </Grid>
                        <Grid item xs={2} textAlign={"right"}>
                          <Typography variant="overline">
                            ({currency(order.payment.discount || "0").format()})
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {+order.payment.salesTax > 0 && (
                      <>
                        <Grid item xs={10}>
                          <Typography variant="body2">Sales Tax</Typography>
                        </Grid>
                        <Grid item xs={2} textAlign={"right"}>
                          <Typography variant="overline">
                            {currency(order.payment.salesTax || "0").format()}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={10}>
                      <Typography variant="body2">Tip</Typography>
                    </Grid>
                    <Grid item xs={2} textAlign={"right"}>
                      <Typography variant="overline">
                        {currency(order.payment.tip || "0").format()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box
                  py={2}
                  sx={{ borderTop: "1px dashed #DEDEDE", borderBottom: "1px dashed #DEDEDE" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={10}>
                      <Typography variant="body2" sx={{ fontWeight: "700" }}>
                        TOTAL AMOUNT DUE
                      </Typography>
                    </Grid>
                    <Grid item xs={2} textAlign={"right"}>
                      <Typography variant="overline">
                        {currency(order.payment.total).format()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box py={2} sx={{ borderBottom: "1px dashed #DEDEDE" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={10}>
                      <Typography variant="body2">Paid</Typography>
                    </Grid>
                    <Grid item xs={2} textAlign={"right"}>
                      <Typography variant="overline">
                        ({currency(order.payment.paid).format()})
                      </Typography>
                    </Grid>
                    <Grid item xs={10}>
                      <Typography variant="body2">Balance Due</Typography>
                    </Grid>
                    <Grid item xs={2} textAlign={"right"}>
                      <Typography variant="overline">
                        {currency(order.payment.due || "0").format()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                {order.payment.refund && (
                  <Box>
                    {order.payment.refund.map((o) => (
                      <Box py={2} sx={{ borderBottom: "1px dashed #DEDEDE", color: "red" }}>
                        <Grid container spacing={2}>
                          <Grid item xs={10}>
                            <Typography variant="body2">REFUND</Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {moment(o.ts).format("LLL")}
                            </Typography>
                          </Grid>
                          <Grid item xs={2} textAlign={"right"}>
                            <Typography variant="overline">
                              {currency(o.amount).format()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Box my={2}>
                <StorePolicy />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button color="primary" variant="contained" onClick={handlePrint} fullWidth>
              PRINT
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
